/**
 * Custom Node Component (New - BaseNode Implementation)
 * Standard rectangle node with gradient header
 */

import React from 'react';
import { ZapIcon, ExecutionStatusIcon } from '@bizfirst/common-react';

// Pin icon — not in @bizfirst/common-react icon set
const PinIcon = ({ size = 11, color = 'white' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z"/>
  </svg>
);
import { NodeLeftIcon, NodeBrandImage } from '../NodeIcon';
import { NodeProps } from 'reactflow';
import { WorkflowNode, getNodeDisplayProps } from '@flow-studio/types';
import { BaseNodeClass, withNodeHooks, NodeConfig } from './Base/BaseNode';
import { colors } from '../../styles/theme';

export class CustomNodeClass extends BaseNodeClass {
  protected config(): NodeConfig {
    return {
      ...super.config(),
      showToolbarOnClick: true,
      showToolbarOnHover: false,
      customClassName: 'custom-node',
    };
  }

  protected getClassName(): string {
    const { selected, data } = this.props;
    return `custom-node ${selected ? 'selected' : ''} ${data.processElement?.isDisabled ? 'disabled' : ''}`;
  }

  protected getContainerStyle(): React.CSSProperties {
    const { data } = this.props;
    const isCollapsed = this.state.isCollapsed;
    const rawStyle = (data.processElement?.configuration?.designer?.ui?.style ?? {}) as React.CSSProperties;
    // Strip explicit dimensions — React Flow measures node size from DOM content
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { width: _w, height: _h, ...userStyle } = rawStyle as any;

    const handleMinH = this.computeHandleMinHeight();

    return {
      position: 'relative',
      opacity: data.processElement?.isDisabled ? 0.6 : 1,
      minHeight: isCollapsed ? '40px' : `${handleMinH}px`,
      maxHeight: isCollapsed ? '50px' : undefined,
      ...userStyle,
    };
  }

  private buildHeader(collapsed = false): React.ReactNode {
    const { data } = this.props;
    const display = getNodeDisplayProps(data);
    const explicitColor = data.processElement?.configuration?.designer?.ui?.color;
    const headerBackground = explicitColor
      ? `linear-gradient(135deg, ${explicitColor} 0%, ${explicitColor}bb 100%)`
      : this.getCategoryGradient(display.category || display.color);
    const headerStyle = (data.processElement?.configuration?.designer?.ui?.headerStyle ?? {}) as React.CSSProperties;

    return (
      <div
        className={`custom-node-header bf-node-header${collapsed ? ' collapsed' : ''}`}
        style={{ background: headerBackground, ...headerStyle }}
      >
        <span className="custom-node-icon">
          <NodeLeftIcon icon={display.icon} category={display.category} size={16} color="white" />
        </span>
        <span className="custom-node-title bf-node-header-title" title={display.displayName}>
          {display.displayName}
        </span>
        {display.isTrigger && (
          <span className="custom-node-trigger-badge"><ZapIcon size={12} /> Trigger</span>
        )}
        {data.processElement?.pinnedData && (
          <span title="Output pinned" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, opacity: 0.85 }}>
            <PinIcon size={11} color="white" />
          </span>
        )}
        <NodeBrandImage
          headerImageUrl={display.headerImageUrl}
          size={30}
          className="bf-node-header-image"
          style={{ borderRadius: '4px', marginLeft: 'auto' }}
        />
      </div>
    );
  }

  protected renderCollapsedContent(): React.ReactNode {
    return (
      <>
        {this.buildHeader(true)}
        {this.renderExecutionStatusBadge()}
      </>
    );
  }

  protected renderContent(): React.ReactNode {
    const { data } = this.props;
    const display = getNodeDisplayProps(data);

    return (
      <>
        {this.buildHeader()}

        {/* Body */}
        <div className="custom-node-body bf-node-body nodrag">
          {display.description && (
            <div className="custom-node-description" title={display.description}>
              {display.description}
            </div>
          )}
          {this.renderExecutionStatus()}
          {display.bodyImageUrl && (
            <img
              src={display.bodyImageUrl}
              style={{ display: 'block', marginLeft: 'auto', marginTop: '6px', height: '40px', maxWidth: '90%', objectFit: 'contain', borderRadius: '4px' }}
              alt=""
            />
          )}
        </div>

        {/* Execution Status Badge Overlay */}
        {this.renderExecutionStatusBadge()}
      </>
    );
  }

  private renderExecutionStatusBadge(): React.ReactNode {
    const { data, designerMode } = this.props;

    // Only show in execution/evaluation mode
    if (designerMode === 'design') return null;

    const executionStatus = data.runtime?.executionStatus;
    const executionMode = data.runtime?.executionMode;

    const statusConfig: Record<string, { color: string; label: string }> = {
      running:  { color: '#3b82f6', label: 'In Progress' },
      success:  { color: '#10b981', label: 'Completed' },
      failed:   { color: '#ef4444', label: 'Failed' },
      skipped:  { color: '#6b7280', label: 'Skipped' },
      waiting:  { color: '#f59e0b', label: 'Waiting' },
      retrying: { color: '#f59e0b', label: 'Retrying' },
      timeout:  { color: '#ef4444', label: 'Timeout' },
    };

    let status: string | undefined = executionStatus;
    let config = status ? statusConfig[status] : null;

    if (!config && executionMode) {
      status = 'not-started';
      config = { color: '#9ca3af', label: 'Not Started' };
    }

    if (!config || !status) return null;

    // Don't show badge after completion (success, failed, skipped, timeout)
    // Keep showing for active states: running, waiting, retrying, not-started
    const completionStates = ['success', 'failed', 'skipped', 'timeout'];
    if (completionStates.includes(status)) {
      return null;
    }

    return (
      <div
        style={{
          position: 'absolute',
          bottom: '-8px',
          left: '-8px',
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: config.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 2px 8px ${config.color}80`,
          zIndex: 10,
          animation: executionStatus === 'running' ? 'pulse-badge 2s ease-in-out infinite' : 'none',
        }}
        title={config.label}
      >
        <ExecutionStatusIcon status={status} size={14} color="white" />
      </div>
    );
  }

  private renderExecutionStatus(): React.ReactNode {
    const { data } = this.props;
    const executionStatus = data.runtime?.executionStatus;
    if (!executionStatus || executionStatus === 'idle') return null;

    return (
      <div className={`custom-node-status ${executionStatus}`}>
        {executionStatus === 'running' && <ZapIcon size={14} />}
        {executionStatus === 'success' && ''}
        {executionStatus === 'error' && ''}
        <span style={{ textTransform: 'capitalize' }}>
          {executionStatus}
        </span>
      </div>
    );
  }

  private getCategoryGradient(
    categoryColor: string | { from: string; to: string } | undefined
  ): string {
    if (typeof categoryColor === 'string') {
      const themeColor = colors[categoryColor as keyof typeof colors];
      // @ts-ignore
      if (themeColor && themeColor.from) {
        // @ts-ignore
        return `linear-gradient(135deg, ${themeColor.from} 0%, ${themeColor.to} 100%)`;
      }
      return `linear-gradient(135deg, ${categoryColor} 0%, ${categoryColor} 100%)`;
    }
    if (!categoryColor) {
      return 'linear-gradient(135deg, rgb(1, 3, 12) 0%, rgb(1, 4, 16) 100%)';
    }
    return `linear-gradient(135deg, ${categoryColor.from} 0%, ${categoryColor.to} 100%)`;
  }
}

export const CustomNode: React.FC<NodeProps<WorkflowNode['data']>> =
  withNodeHooks(CustomNodeClass);
